// One Noise Lab training set (a book): drop its BEFORE and AFTER folders (and, optionally, the engineer's
// marker lists), check how the chapters pair up, upload, and follow each chapter through extraction.
// Dropping only the BEFORE recordings makes chapters with just the recording, as a user would bring them:
// the models can be tried on them straight away, and each edited version can be added later (a blind test).
import { useEffect, useMemo, useRef, useState } from "react";
import { askToConfirm } from "../components/confirm";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadViaUrl, fetchBlobUrl, multipartUploadTo, upload } from "../api/client";
import type { LabMarker, LabMarkersSummary, LabPair, LabPairingPreview, LabReview, LabSetDetail } from "../api/types";
import { useMe } from "../lib/me";
import { filesFromDrop, visible } from "../lib/folderDrop";
import { hoursMinutes } from "./NoiseLab";

const BASE = "/api/modules/noise_lab";
const AUDIO_RE = /\.(wav|aiff?|flac|mp3|m4a)$/i;
const MARKER_RE = /\.(csv|txt)$/i;

function mb(bytes: number) {
  return bytes ? `${(bytes / 1048576).toFixed(0)} MB` : "—";
}
function mmss(seconds?: number | null) {
  if (seconds == null) return "—";
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
function clock(seconds: number) {
  const m = Math.floor(seconds / 60);
  return `${m}:${(seconds - m * 60).toFixed(1).padStart(4, "0")}`;
}
const KIND_LABEL: Record<string, string> = {
  "mouth-like": "mouth noise", "LF event": "low thump", "long low-level edit": "long clean-up",
  "artefact": "tiny artefact", "pause trimmed": "pause shortened", "pause expanded": "pause lengthened",
};

type Kind = "before" | "after" | "markers";
interface Progress { pct: number; state: "uploading" | "done" | "error"; message?: string }

async function uploadSide(setId: string, pairId: string, side: "before" | "after", file: File,
                          onPct: (p: number) => void, signal: AbortSignal): Promise<LabPair> {
  const base = `${BASE}/sets/${setId}/pairs/${pairId}/${side}`;
  const init = await api<{ storage: string; key?: string; upload_id?: string }>(
    `${base}/multipart/create`, { method: "POST", signal });
  if (init.storage === "r2" && init.key && init.upload_id) {
    return multipartUploadTo<LabPair>(`${base}/multipart`, file, init.key, init.upload_id, onPct, signal);
  }
  const form = new FormData();
  form.append("file", file);
  return upload<LabPair>(`${base}/file`, form, (p) => onPct(p));
}

function uploadMarkers(setId: string, pairId: string, file: File): Promise<LabPair> {
  const form = new FormData();
  form.append("file", file);
  return upload<LabPair>(`${BASE}/sets/${setId}/pairs/${pairId}/markers`, form);
}

const TITLES: Record<Kind, string> = { before: "Before the edit", after: "After the edit (optional)", markers: "Markers (optional)" };

function DropSide({ kind, files, onFiles }: { kind: Kind; files: File[]; onFiles: (f: File[]) => void }) {
  const [drag, setDrag] = useState(false);
  const folderRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<HTMLInputElement>(null);
  const re = kind === "markers" ? MARKER_RE : AUDIO_RE;
  const useful = files.filter((f) => re.test(f.name));
  const total = useful.reduce((a, f) => a + f.size, 0);
  const noun = kind === "markers" ? "marker list" : "audio file";
  return (
    <div
      className={`dropzone lab-drop${drag ? " drag" : ""}${files.length ? " filled" : ""}${kind === "markers" ? " lab-drop-markers" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        filesFromDrop(e.dataTransfer).then(onFiles);
      }}
    >
      <div className="lab-drop-title">{TITLES[kind]}</div>
      {files.length ? (
        <div className="lab-drop-count">
          <strong>{useful.length}</strong> {noun}{useful.length === 1 ? "" : "s"}{kind !== "markers" && <> · {mb(total)}</>}
          {files.length > useful.length && <span className="muted"> · {files.length - useful.length} other</span>}
          <button type="button" className="btn ghost sm" onClick={() => onFiles([])}>Clear</button>
        </div>
      ) : (
        <div className="muted lab-drop-hint">
          {kind === "markers" ? "Audition marker exports, one per chapter"
            : kind === "after" ? "Leave empty to try the models on the recordings alone. Drop the folder here, or"
            : "Drop the folder here, or"}
        </div>
      )}
      <div className="lab-drop-actions">
        <button type="button" className="btn sm" onClick={() => folderRef.current?.click()}>Choose folder</button>
        <button type="button" className="btn ghost sm" onClick={() => filesRef.current?.click()}>Choose files</button>
      </div>
      <input ref={folderRef} type="file" hidden multiple
             {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
             onChange={(e) => { onFiles(visible(Array.from(e.target.files || []))); e.target.value = ""; }} />
      <input ref={filesRef} type="file" hidden multiple
             accept={kind === "markers" ? ".csv,.txt" : ".wav,.aif,.aiff,.flac,.mp3,.m4a"}
             onChange={(e) => { onFiles(visible(Array.from(e.target.files || []))); e.target.value = ""; }} />
    </div>
  );
}

function statusOf(p: LabPair, prog?: Progress): { label: string; cls: string } {
  if (prog && prog.state === "uploading") return { label: `Uploading ${prog.pct}%`, cls: "lab-uploading" };
  if (prog && prog.state === "error") return { label: "Upload failed", cls: "lab-failed" };
  switch (p.status) {
    case "uploading": return { label: "Not uploaded", cls: "lab-idle" };
    case "queued": return { label: "Waiting", cls: "lab-idle" };
    case "extracting": return { label: "Finding edits…", cls: "lab-extracting" };
    case "done": return { label: "Done", cls: "lab-done" };
    case "ready": return { label: "Recording only", cls: "lab-done" };
    default: return { label: "Failed", cls: "lab-failed" };
  }
}

type Panel = { id: string; kind: "report" | "markers" } | null;

export function NoiseLabSet() {
  const { setId = "" } = useParams();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [before, setBefore] = useState<File[]>([]);
  const [after, setAfter] = useState<File[]>([]);
  const [markers, setMarkers] = useState<File[]>([]);
  const [preview, setPreview] = useState<LabPairingPreview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, Progress>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [reportText, setReportText] = useState<string>("");
  const [panelMarkers, setPanelMarkers] = useState<{ markers: LabMarker[]; summary: LabMarkersSummary | null } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const q = useQuery({
    queryKey: ["lab-set", setId],
    queryFn: () => api<LabSetDetail>(`${BASE}/sets/${setId}`),
    enabled: !!me?.is_superadmin && !!setId,
    refetchInterval: (qq) => (busy || qq.state.data?.pairs.some((p) => p.status === "queued" || p.status === "extracting") ? 3000 : false),
  });
  const existing = q.data?.pairs ?? [];
  // Markers dropped on their own go to the chapters already in the set.
  const markersOnly = markers.length > 0 && !before.length && !after.length && existing.length > 0;

  // Ask the server how the folders pair up whenever one changes.
  useEffect(() => {
    setPreview(null);
    setPreviewErr(null);
    const audioReady = before.length > 0;               // no AFTER folder: chapters with just the recording
    if (!audioReady && !markersOnly) return;
    let live = true;
    const body = audioReady
      ? { before: before.map((f) => ({ name: f.name, size: f.size })), after: after.map((f) => ({ name: f.name, size: f.size })) }
      : { chapters: existing.map((p) => ({ before: p.before_name, after: p.after_name, stem: p.stem })) };
    api<LabPairingPreview>(`${BASE}/pairing`, {
      method: "POST",
      body: JSON.stringify({ ...body, markers: markers.map((f) => ({ name: f.name, size: f.size })) }),
    }).then((r) => { if (live) setPreview(r); }, (e) => { if (live) setPreviewErr((e as Error).message); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [before, after, markers, markersOnly, existing.length]);

  const byName = useMemo(() => {
    const m = { before: new Map<string, File>(), after: new Map<string, File>(), markers: new Map<string, File>() };
    for (const f of before) if (!m.before.has(f.name)) m.before.set(f.name, f);
    for (const f of after) if (!m.after.has(f.name)) m.after.set(f.name, f);
    for (const f of markers) if (!m.markers.has(f.name)) m.markers.set(f.name, f);
    return m;
  }, [before, after, markers]);

  const markerFor = (bname: string, aname: string): File | undefined => {
    const hit = preview?.pairs.find((p) => p.before === bname && p.after === aname);
    return hit?.markers ? byName.markers.get(hit.markers) : undefined;
  };

  function finish(bits: string[], clear: boolean) {
    setBusy(false);
    abortRef.current = null;
    qc.invalidateQueries({ queryKey: ["lab-set", setId] });
    qc.invalidateQueries({ queryKey: ["lab-sets"] });
    setNotice((n) => n ?? bits.join(" · "));
    if (clear) { setBefore([]); setAfter([]); setMarkers([]); }
  }

  async function startUpload() {
    if (!preview?.pairs.length) return;
    setBusy(true);
    setNotice(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let failed = 0, skipped = 0, sent = 0, marked = 0;
    try {
      const made = await api<{ pairs: LabPair[] }>(`${BASE}/sets/${setId}/pairs`, {
        method: "POST",
        body: JSON.stringify({
          pairs: preview.pairs.map((p) => ({
            stem: p.stem, before_name: p.before, after_name: p.after, before_size: p.before_size, after_size: p.after_size,
          })),
        }),
      });
      qc.invalidateQueries({ queryKey: ["lab-set", setId] });
      for (const pair of made.pairs) {
        if (ctrl.signal.aborted) break;
        const mf = markerFor(pair.before_name, pair.after_name);
        if (pair.skip) {
          skipped++;
          if (mf) { try { await uploadMarkers(setId, pair.id, mf); marked++; } catch (e) { failed++; setNotice(`${mf.name}: ${(e as Error).message}`); } }
          continue;
        }
        const bf = byName.before.get(pair.before_name);
        const af = pair.after_name ? byName.after.get(pair.after_name) : undefined;
        if (!bf || (pair.after_name && !af)) { failed++; continue; }
        const total = Math.max(1, bf.size + (af?.size ?? 0));
        const set = (pr: Progress) => setProgress((old) => ({ ...old, [pair.id]: pr }));
        set({ pct: 0, state: "uploading" });
        try {
          // markers first: they are tiny, and then they are matched the moment extraction finishes
          if (mf) { await uploadMarkers(setId, pair.id, mf); marked++; }
          await uploadSide(setId, pair.id, "before", bf, (p) => {
            set({ pct: Math.round(((p / 100) * bf.size / total) * 100), state: "uploading" });
          }, ctrl.signal);
          if (af) {
            await uploadSide(setId, pair.id, "after", af, (p) => {
              set({ pct: Math.round(((bf.size + (p / 100) * af.size) / total) * 100), state: "uploading" });
            }, ctrl.signal);
          }
          set({ pct: 100, state: "done" });
          sent++;
        } catch (e) {
          if ((e as DOMException)?.name === "AbortError") break;
          failed++;
          set({ pct: 0, state: "error", message: (e as Error).message });
        }
        qc.invalidateQueries({ queryKey: ["lab-set", setId] });
      }
    } catch (e) {
      setNotice((e as Error).message);
    }
    const bits = [`${sent} uploaded`];
    if (skipped) bits.push(`${skipped} already in the set`);
    if (marked) bits.push(`${marked} marker list${marked === 1 ? "" : "s"} attached`);
    if (failed) bits.push(`${failed} failed`);
    if (ctrl.signal.aborted) bits.push("stopped");
    finish(bits, !failed && !ctrl.signal.aborted);
  }

  async function attachMarkersOnly() {
    if (!preview) return;
    setBusy(true);
    setNotice(null);
    let ok = 0, failed = 0;
    for (const p of preview.pairs) {
      if (!p.markers) continue;
      const pair = existing.find((x) => x.before_name === p.before && x.after_name === p.after);
      const f = byName.markers.get(p.markers);
      if (!pair || !f) continue;
      try { await uploadMarkers(setId, pair.id, f); ok++; } catch (e) { failed++; setNotice(`${f.name}: ${(e as Error).message}`); }
    }
    const bits = [`${ok} marker list${ok === 1 ? "" : "s"} attached`];
    if (failed) bits.push(`${failed} failed`);
    finish(bits, !failed);
  }

  async function togglePanel(p: LabPair, kind: "report" | "markers") {
    if (panel?.id === p.id && panel.kind === kind) { setPanel(null); return; }
    setPanel({ id: p.id, kind });
    if (kind === "report") {
      setReportText("Loading…");
      try {
        const url = await fetchBlobUrl(`${BASE}/sets/${setId}/pairs/${p.id}/report`);
        setReportText(await (await fetch(url)).text());
        URL.revokeObjectURL(url);
      } catch (e) {
        setReportText(`Couldn't load the report: ${(e as Error).message}`);
      }
    } else {
      setPanelMarkers(null);
      try {
        setPanelMarkers(await api(`${BASE}/sets/${setId}/pairs/${p.id}/markers`));
      } catch (e) {
        setNotice((e as Error).message);
      }
    }
  }

  async function addMarkersTo(p: LabPair, file: File) {
    setNotice(null);
    try {
      await uploadMarkers(setId, p.id, file);
      setNotice(`${file.name} attached to ${p.stem}`);
      if (panel?.id === p.id && panel.kind === "markers") {
        setPanelMarkers(await api(`${BASE}/sets/${setId}/pairs/${p.id}/markers`));
      }
    } catch (e) {
      setNotice(`${file.name}: ${(e as Error).message}`);
    }
    qc.invalidateQueries({ queryKey: ["lab-set", setId] });
  }

  async function addAfter(p: LabPair, file: File) {
    setNotice(null);
    const set = (pr: Progress) => setProgress((old) => ({ ...old, [p.id]: pr }));
    set({ pct: 0, state: "uploading" });
    try {
      await api(`${BASE}/sets/${setId}/pairs/${p.id}/after`, {
        method: "POST", body: JSON.stringify({ after_name: file.name, after_size: file.size }),
      });
      qc.invalidateQueries({ queryKey: ["lab-set", setId] });
      await uploadSide(setId, p.id, "after", file, (pct) => set({ pct, state: "uploading" }), new AbortController().signal);
      set({ pct: 100, state: "done" });
      setNotice(`${file.name} added to ${p.stem}: finding the edits, then any trials on it run again to compare`);
    } catch (e) {
      set({ pct: 0, state: "error", message: (e as Error).message });
    }
    qc.invalidateQueries({ queryKey: ["lab-set", setId] });
  }

  async function act(p: LabPair, what: "retry" | "delete" | "unmark") {
    if (what === "delete" && !(await askToConfirm(`Delete "${p.stem}" and its ${p.after_name ? "two recordings" : "recording"} from this set?`,
      { yes: "Delete", danger: true }))) return;
    if (what === "unmark" && !(await askToConfirm(`Remove the marker list from "${p.stem}"?`, { yes: "Remove", danger: true }))) return;
    try {
      const path = `${BASE}/sets/${setId}/pairs/${p.id}${what === "retry" ? "/retry" : what === "unmark" ? "/markers" : ""}`;
      await api(path, { method: what === "retry" ? "POST" : "DELETE" });
      if (what === "unmark" && panel?.id === p.id) setPanel(null);
    } catch (e) {
      setNotice((e as Error).message);
    }
    qc.invalidateQueries({ queryKey: ["lab-set", setId] });
  }

  async function downloadManifest() {
    const m = await api<unknown>(`${BASE}/sets/${setId}/manifest`);
    const url = URL.createObjectURL(new Blob([JSON.stringify(m, null, 1)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${q.data?.name ?? "set"} manifest.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function deleteSet() {
    if (!q.data) return;
    if (!(await askToConfirm(`Delete the set "${q.data.name}" and all ${q.data.n_pairs} of its chapters? The recordings are removed too.`,
      { yes: "Delete set", danger: true }))) return;
    try {
      await api(`${BASE}/sets/${setId}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["lab-sets"] });
      nav("/admin/noise-lab");
    } catch (e) {
      setNotice((e as Error).message);
    }
  }

  if (me && !me.is_superadmin) return <Navigate to="/" replace />;
  if (q.isError) return <div className="page lab-error">Couldn't load this set: {(q.error as Error).message}</div>;
  if (!q.data) return <div className="page muted">Loading…</div>;
  const s = q.data;
  const count = (fs: File[], re: RegExp) => fs.filter((f) => re.test(f.name)).length;
  const withMarkers = preview?.pairs.filter((p) => p.markers).length ?? 0;
  const recordingsOnly = !markersOnly && !!preview?.pairs.length && preview.pairs.every((p) => p.how === "recording");
  const showMarkerCol = markers.length > 0;

  return (
    <div className="page admin-page lab-page">
      <div className="page-head">
        <div>
          <Link to="/admin/noise-lab" className="lab-back">Noise Lab</Link>
          <h1>{s.name}</h1>
          <div className="stat-chips">
            <span className="stat-chip">{s.n_pairs} chapter{s.n_pairs === 1 ? "" : "s"}</span>
            <span className="stat-chip">{s.n_done} processed</span>
            {s.n_recording_only > 0 && <span className="stat-chip">{s.n_recording_only} recording only</span>}
            {s.n_waiting > 0 && <span className="stat-chip">{s.n_waiting} in progress</span>}
            {s.n_failed > 0 && <span className="stat-chip lab-failed-t">{s.n_failed} failed</span>}
            <span className="stat-chip">{s.n_edits.toLocaleString()} edits found</span>
            {s.n_markers > 0 && <span className="stat-chip">{s.n_markers_matched.toLocaleString()} of {s.n_markers.toLocaleString()} markers on an edit</span>}
            <span className="stat-chip">{hoursMinutes(s.processed_seconds)} of audio</span>
          </div>
        </div>
        <div className="lab-head-actions">
          <button className="btn ghost sm" onClick={downloadManifest} disabled={!s.n_done && !s.n_recording_only}
                  title="Links to every processed chapter's recordings, edits and markers, for training">
            Download manifest
          </button>
          <button className="btn ghost sm lab-danger" onClick={deleteSet} disabled={busy}>Delete set</button>
        </div>
      </div>

      <section className="card lab-add">
        <div className="card-head">
          <div className="card-head-title">Add recordings</div>
          <div className="muted lab-add-hint">
            Drop the book's two folders, and its marker lists if you have them. Chapters are matched by name.
            To try a model the way a user would, drop only the recordings from before the edit: add each edited
            version later from its row, and the lab compares the markers with what you fixed.
          </div>
        </div>
        <div className="lab-drops">
          <DropSide kind="before" files={before} onFiles={setBefore} />
          <DropSide kind="after" files={after} onFiles={setAfter} />
          <DropSide kind="markers" files={markers} onFiles={setMarkers} />
        </div>
        {previewErr && <div className="lab-error lab-inset">{previewErr}</div>}
        {preview && (
          <div className="lab-preview">
            <div className="lab-preview-head">
              {markersOnly ? (
                <strong>{withMarkers} of {count(markers, MARKER_RE)} marker lists match a chapter already in this set</strong>
              ) : recordingsOnly ? (
                <>
                  <strong>{preview.pairs.length} recording{preview.pairs.length === 1 ? "" : "s"}, each its own chapter</strong>
                  <span className="muted"> · no edited versions yet: add each one later from its row</span>
                  {showMarkerCol && <span className="muted"> · {withMarkers} with markers</span>}
                </>
              ) : (
                <>
                  <strong>{preview.pairs.length} of {Math.max(count(before, AUDIO_RE), count(after, AUDIO_RE))} chapters paired</strong>
                  {showMarkerCol && <span className="muted"> · {withMarkers} with markers</span>}
                  {preview.pairs.some((p) => p.how === "number") && (
                    <span className="muted"> · some matched by chapter number only, please check those</span>
                  )}
                </>
              )}
            </div>
            {(preview.unpaired_before.length > 0 || preview.unpaired_after.length > 0 || (preview.unpaired_markers?.length ?? 0) > 0) && (
              <div className="lab-warn">
                {!markersOnly && preview.unpaired_before.length > 0 && (
                  <div>No match for {preview.unpaired_before.length} file{preview.unpaired_before.length === 1 ? "" : "s"} from before the edit: {preview.unpaired_before.join(", ")}</div>
                )}
                {!markersOnly && preview.unpaired_after.length > 0 && (
                  <div>No match for {preview.unpaired_after.length} file{preview.unpaired_after.length === 1 ? "" : "s"} from after the edit: {preview.unpaired_after.join(", ")}</div>
                )}
                {(preview.unpaired_markers?.length ?? 0) > 0 && (
                  <div>No chapter for {preview.unpaired_markers!.length} marker list{preview.unpaired_markers!.length === 1 ? "" : "s"}: {preview.unpaired_markers!.join(", ")}</div>
                )}
                <div className="muted">Unmatched files are left out. Rename them to match and drop them again.</div>
              </div>
            )}
            {preview.pairs.length > 0 && (
              <div className="lab-tablewrap lab-preview-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Chapter</th>
                      {!markersOnly && <><th>Before the edit</th><th>After the edit</th><th className="r">Size</th><th>Matched by</th></>}
                      {showMarkerCol && <th>Markers</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.pairs.filter((p) => !markersOnly || p.markers).map((p) => (
                      <tr key={p.before + "|" + p.after} className={!markersOnly && p.how === "number" ? "lab-bynumber" : undefined}>
                        <td>{p.stem}</td>
                        {!markersOnly && (
                          <>
                            <td className="lab-fname">{p.before}</td>
                            <td className="lab-fname">{p.after || <span className="muted">added later</span>}</td>
                            <td className="r num">{mb(p.before_size + p.after_size)}</td>
                            <td className="muted">{p.how === "name" ? "name" : p.how === "recording" ? "recording only" : "chapter number"}</td>
                          </>
                        )}
                        {showMarkerCol && <td className="lab-fname">{p.markers ?? <span className="muted">none</span>}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="lab-preview-foot">
              {busy && !markersOnly ? (
                <button className="btn" onClick={() => abortRef.current?.abort()}>Stop uploading</button>
              ) : markersOnly ? (
                <button className="btn primary" disabled={!withMarkers || busy} onClick={attachMarkersOnly}>
                  {busy ? "Attaching…" : `Attach ${withMarkers} marker list${withMarkers === 1 ? "" : "s"}`}
                </button>
              ) : (
                <button className="btn primary" disabled={!preview.pairs.length} onClick={startUpload}>
                  Upload {preview.pairs.length} chapter{preview.pairs.length === 1 ? "" : "s"}
                </button>
              )}
            </div>
          </div>
        )}
        {markers.length > 0 && !before.length && !after.length && !existing.length && (
          <div className="muted lab-inset">Marker lists need chapters: drop the recordings too.</div>
        )}
        {notice && <div className="lab-notice">{notice}</div>}
      </section>

      <ReviewsCard setId={setId} pairs={s.pairs} />

      <section className="card lab-pairs">
        <div className="card-head"><div className="card-head-title">Chapters</div></div>
        {s.pairs.length === 0 ? (
          <div className="empty-state">No chapters yet.</div>
        ) : (
          <div className="lab-tablewrap">
            <table className="table lab-table">
              <thead>
                <tr>
                  <th>Chapter</th>
                  <th>Status</th>
                  <th className="r" title="Short noises removed: clicks, smacks, saliva">Mouth noises</th>
                  <th className="r" title="Low-frequency thumps and knocks">Low thumps</th>
                  <th className="r" title="Longer low-level clean-ups, e.g. a whole pause swept">Long clean-ups</th>
                  <th className="r" title="Pauses shortened or lengthened">Pause edits</th>
                  <th className="r">Length</th>
                  <th title="Your marker list for the chapter, and how many markers point at an edit">Markers</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {s.pairs.map((p) => (
                  <PairRows key={p.id} p={p} setId={setId} prog={progress[p.id]} panel={panel}
                            report={reportText} markers={panelMarkers}
                            onPanel={(k) => togglePanel(p, k)}
                            onEdits={() => downloadViaUrl(`${BASE}/sets/${setId}/pairs/${p.id}/files/events.json/url`).catch((e) => setNotice((e as Error).message))}
                            onAddMarkers={(f) => addMarkersTo(p, f)}
                            onAddAfter={(f) => addAfter(p, f)}
                            onAct={(w) => act(p, w)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function PairRows(props: {
  p: LabPair; setId: string; prog?: Progress; panel: Panel; report: string;
  markers: { markers: LabMarker[]; summary: LabMarkersSummary | null } | null;
  onPanel: (k: "report" | "markers") => void; onEdits: () => void; onAddMarkers: (f: File) => void;
  onAddAfter: (f: File) => void;
  onAct: (w: "retry" | "delete" | "unmark") => void;
}) {
  const { p } = props;
  const st = statusOf(p, props.prog);
  const up = props.prog?.state === "uploading";
  const err = props.prog?.message || p.error;
  const done = p.status === "done";
  const k = p.summary?.by_kind;
  const ms = p.markers_summary;
  const fileRef = useRef<HTMLInputElement>(null);
  const afterRef = useRef<HTMLInputElement>(null);
  const openKind = props.panel?.id === p.id ? props.panel.kind : null;
  // just the recording, or an edited version whose upload never finished: the edited version can be (re)added
  const canAddAfter = !up && p.before_uploaded && (p.status === "ready" || (p.status === "uploading" && !!p.after_name && !p.after_uploaded));
  return (
    <>
      <tr>
        <td>
          <div className="lab-stem">{p.stem}</div>
          <div className="muted lab-fname" title={p.after_name ? `${p.before_name} → ${p.after_name}` : `${p.before_name} (no edited version yet)`}>{p.before_name}</div>
        </td>
        <td>
          <span className={`lab-pill ${st.cls}`}>{st.label}</span>
          {up && <div className="progress-track lab-bar"><div className="progress-fill" style={{ width: `${props.prog?.pct ?? 0}%` }} /></div>}
          {err && (p.status === "failed" || !up) && <div className="lab-err-line" title={err}>{err}</div>}
        </td>
        {[k?.["mouth-like"], k?.["LF event"], k?.["long low-level edit"], p.summary?.n_pause_edits].map((c, i) => (
          <td key={i} className="r num">{done ? (c ?? 0) : "—"}</td>
        ))}
        <td className="r num">{mmss(p.summary?.duration_s)}</td>
        <td className="lab-markercell">
          {p.n_markers > 0 ? (
            <button className={`btn ghost sm lab-markerbtn${openKind === "markers" ? " on" : ""}`} onClick={() => props.onPanel("markers")}
                    title={p.status === "ready"
                      ? `${p.markers_name ?? ""}: matched to your edits once the edited version is added. Click to see each one.`
                      : `${p.markers_name ?? ""}: of the markers read from this file, how many point at one of your edits. Click to see each one.`}>
              {ms ? `${ms.n_matched} of ${ms.n_readable} markers on an edit` : `${p.n_markers} marker${p.n_markers === 1 ? "" : "s"}`}
            </button>
          ) : (
            <button className="btn ghost sm" onClick={() => fileRef.current?.click()}>Add</button>
          )}
          <input ref={fileRef} type="file" hidden accept=".csv,.txt"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) props.onAddMarkers(f); e.target.value = ""; }} />
        </td>
        <td className="r lab-actions">
          {canAddAfter && (
            <button className="btn ghost sm" onClick={() => afterRef.current?.click()}
                    title="The recording after your edit: the lab finds the edits and compares every trial's markers with them">
              {p.status === "ready" ? "Add edited version" : "Upload edited version again"}
            </button>
          )}
          <input ref={afterRef} type="file" hidden accept=".wav,.aif,.aiff,.flac,.mp3,.m4a"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) props.onAddAfter(f); e.target.value = ""; }} />
          {done && <button className="btn ghost sm" onClick={() => props.onPanel("report")}>{openKind === "report" ? "Hide report" : "Report"}</button>}
          {done && <button className="btn ghost sm" onClick={props.onEdits}>Edits</button>}
          {(p.status === "failed" || done) && p.before_uploaded && p.after_uploaded && (
            <button className="btn ghost sm" onClick={() => props.onAct("retry")} title="Run the extraction again">Re-run</button>
          )}
          {p.status !== "extracting" && !up && (
            <button className="btn ghost sm lab-danger" onClick={() => props.onAct("delete")}>Delete</button>
          )}
        </td>
      </tr>
      {openKind === "report" && (
        <tr className="lab-reportrow"><td colSpan={9}><pre className="lab-report">{props.report}</pre></td></tr>
      )}
      {openKind === "markers" && (
        <tr className="lab-reportrow">
          <td colSpan={9}>
            <MarkersPanel p={p} data={props.markers}
                          onReplace={() => fileRef.current?.click()} onRemove={() => props.onAct("unmark")} />
          </td>
        </tr>
      )}
    </>
  );
}

function MarkersPanel({ p, data, onReplace, onRemove }: {
  p: LabPair; data: { markers: LabMarker[]; summary: LabMarkersSummary | null } | null;
  onReplace: () => void; onRemove: () => void;
}) {
  if (!data) return <div className="muted">Loading…</div>;
  const s = data.summary;
  const timeline = s?.timeline === "after" ? "the recording after the edit"
    : s?.timeline === "before" ? "the recording before the edit" : "either recording (no pause edits, so they're the same)";
  return (
    <div className="lab-markers">
      <div className="lab-markers-head">
        <div>
          <strong>{p.markers_name}</strong>
          {s ? (
            <span className="muted"> · read on {timeline} · {s.n_matched} of {s.n_readable} markers point at an edit ·{" "}
              {s.n_edits_marked} of {s.n_edits} edits have a marker
              {s.n_unreadable > 0 && <> · {s.n_unreadable} with a time that couldn't be read</>}</span>
          ) : (
            <span className="muted"> · {p.status === "ready" ? "matched to your edits once the edited version is added"
              : "matched to the edits once the chapter is processed"}</span>
          )}
        </div>
        <div className="lab-markers-actions">
          <button className="btn ghost sm" onClick={onReplace}>Replace</button>
          <button className="btn ghost sm lab-danger" onClick={onRemove}>Remove</button>
        </div>
      </div>
      <div className="lab-markers-scroll">
        <table className="table lab-markers-table">
          <thead>
            <tr><th>Time</th><th>Marker</th><th>Comment</th><th>Points at</th></tr>
          </thead>
          <tbody>
            {data.markers.map((m) => (
              <tr key={m.row} className={s && m.start != null && !m.match ? "lab-nomatch" : undefined}>
                <td className="num">{m.time_text || "—"}</td>
                <td>{m.name}</td>
                <td className="muted">{m.description}</td>
                <td>
                  {m.error ? <span className="lab-failed-t">{m.error}</span>
                    : !s ? <span className="muted">—</span>
                    : m.match ? <>{KIND_LABEL[m.match.kind] ?? m.match.kind} at {clock(m.match.t0)}
                        {m.match.distance_ms > 0 && <span className="muted"> ({Math.round(m.match.distance_ms)} ms away)</span>}</>
                    : <span className="lab-failed-t">no edit found here</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const REVIEW_KIND_LABEL: Record<string, string> = { edits: "Label my edits", calibration: "Grain calibration", import: "Did I miss these?" };

function ReviewsCard({ setId, pairs }: { setId: string; pairs: LabPair[] }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [what, setWhat] = useState<"edits" | "unmarked">("edits");
  const [chapter, setChapter] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const done = pairs.filter((p) => p.status === "done");
  const reviews = useQuery({
    queryKey: ["lab-reviews", setId],
    queryFn: () => api<{ reviews: LabReview[] }>(`${BASE}/sets/${setId}/reviews`),
  });

  async function create() {
    setBusy(true);
    setErr(null);
    try {
      const body = { kind: "edits", only_unmarked: what === "unmarked", pair_ids: chapter ? [chapter] : null };
      const r = await api<LabReview>(`${BASE}/sets/${setId}/reviews`, { method: "POST", body: JSON.stringify(body) });
      qc.invalidateQueries({ queryKey: ["lab-reviews", setId] });
      nav(`/admin/noise-lab/${setId}/review/${r.id}`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(r: LabReview) {
    if (!(await askToConfirm(`Delete the review "${r.name}"${r.n_answered ? ` and its ${r.n_answered} answers` : ""}?`,
      { yes: "Delete review", danger: true }))) return;
    await api(`${BASE}/sets/${setId}/reviews/${r.id}`, { method: "DELETE" });
    qc.invalidateQueries({ queryKey: ["lab-reviews", setId] });
  }

  const list = reviews.data?.reviews ?? [];
  return (
    <section className="card lab-reviews">
      <div className="card-head">
        <div className="card-head-title">Reviews</div>
        <div className="muted lab-add-hint">
          Judge spots in the browser; every answer becomes a training label. For what the model found that you didn't,
          run a model on the chapter (Models) and use "Did I miss these?".
        </div>
      </div>
      <div className="lab-review-new">
        <select className="input" value={what} onChange={(e) => setWhat(e.target.value as typeof what)} aria-label="What to review">
          <option value="edits">Label my edits (type and how much it matters)</option>
          <option value="unmarked">Label only edits my markers don't describe</option>
        </select>
        <select className="input" value={chapter} onChange={(e) => setChapter(e.target.value)} aria-label="Which chapters">
          <option value="">All processed chapters ({done.length})</option>
          {done.map((p) => <option key={p.id} value={p.id}>{p.stem}</option>)}
        </select>
        <button className="btn primary" onClick={create} disabled={busy || !done.length}>{busy ? "Preparing…" : "Start review"}</button>
      </div>
      {err && <div className="lab-error lab-inset">{err}</div>}
      {list.length > 0 && (
        <div className="lab-tablewrap">
          <table className="table lab-table">
            <thead><tr><th>Review</th><th>Kind</th><th className="r">Answered</th><th></th><th></th></tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/admin/noise-lab/${setId}/review/${r.id}`} className="lab-setlink">{r.name}</Link></td>
                  <td className="muted">{REVIEW_KIND_LABEL[r.kind] ?? r.kind}</td>
                  <td className="r num">{r.n_answered} of {r.n_spots}</td>
                  <td className="lab-review-bar">
                    <div className="progress-track"><div className="progress-fill" style={{ width: `${(r.n_answered / Math.max(1, r.n_spots)) * 100}%` }} /></div>
                  </td>
                  <td className="r lab-actions">
                    <Link className="btn ghost sm" to={`/admin/noise-lab/${setId}/review/${r.id}`}>{r.n_answered ? "Continue" : "Open"}</Link>
                    <button className="btn ghost sm lab-danger" onClick={() => remove(r)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
